import { apiClient } from './client'

export interface MemoryItem {
  id: string
  memory: string
  score?: number
  createdAt?: string
  updatedAt?: string
  /** Creator username when known; omitted for legacy / system rows. */
  createdByUsername?: string
  /**
   * Provider-returned metadata, read back verbatim. `memoryType` (Task 24)
   * distinguishes 'world' | 'experience' | 'reflection'; a 'reflection'
   * memory also carries `reflectsMemoryIds` (Task 25) as a JSON-encoded
   * array string — see `MemoryList`'s reflection badge.
   */
  metadata?: Record<string, unknown>
}

export interface MemoryChange {
  id: string
  memory: string
  kind: 'ADD' | 'UPDATE' | 'DELETE' | 'NONE'
}

export interface MemorySearchRequest {
  projectName: string
  query: string
  limit?: number
  minScore?: number
}

export const memoryApi = {
  async search(request: MemorySearchRequest): Promise<MemoryItem[]> {
    const { memories } = await apiClient.post<{ memories: MemoryItem[] }>('/memory/search', request)
    return memories
  },

  async list(projectName: string, limit = 1000, offset = 0): Promise<MemoryItem[]> {
    // Backend max for GET /memory is 1000 (raised from 100 to match Files tab's
    // "fetch all" pattern); default was formerly omitted (server
    // DEFAULT_LIMIT=10), which hid most project memories in the UI.
    const params = new URLSearchParams({
      projectName,
      limit: String(limit),
      ...(offset ? { offset: String(offset) } : {}),
    })
    const { memories } = await apiClient.get<{ memories: MemoryItem[] }>(
      `/memory?${params.toString()}`
    )
    return [...memories].sort((a, b) => {
      const ta = a.createdAt ?? a.updatedAt ?? ''
      const tb = b.createdAt ?? b.updatedAt ?? ''
      return tb.localeCompare(ta)
    })
  },

  async add(
    projectName: string,
    messages: Array<{ role: 'user' | 'assistant' | 'system'; content: string }>
  ): Promise<MemoryChange[]> {
    const { changes } = await apiClient.post<{ changes: MemoryChange[] }>('/memory', {
      projectName,
      messages,
    })
    return changes
  },

  async update(projectName: string, id: string, text: string): Promise<void> {
    await apiClient.patch(`/memory/${id}`, { projectName, text })
  },

  async remove(projectName: string, id: string): Promise<void> {
    const params = new URLSearchParams({ projectName })
    await apiClient.delete(`/memory/${id}?${params.toString()}`)
  },
}
